import { jsx as _jsx } from "react/jsx-runtime";
import { isValidElement, useMemo } from 'react';
import { Select as AntdSelect, Cascader as AntdCascader, TreeSelect as AntdTreeSelect } from 'antd';
import { Icon } from '../icons/icon.js';
import { resolveCornerPlacement, useUiDirection } from './direction.js';
/** At or under this many options, a Select renders every row (see above). */
export const VIRTUAL_THRESHOLD = 100;
function countOptions(options) {
    if (!Array.isArray(options))
        return 0;
    return options.reduce((total, raw) => {
        const inner = raw && typeof raw === 'object' ? raw.options : undefined;
        return total + (Array.isArray(inner) ? countOptions(inner) : 1);
    }, 0);
}
/**
 * The text written inside a label element — «دکتر رز کریمی» out of
 * `<strong>دکتر رز کریمی</strong>`, the parts of a `<Space>` side by side.
 * Only literal children are read: a component that renders a value from its
 * props (`<Money rials={…} />`) contributes nothing, which is why `title` is
 * still the better source when a label is mostly numbers.
 */
function nodeText(node) {
    return rawText(node).replace(/\s+/g, ' ').trim();
}
function rawText(node) {
    if (typeof node === 'string')
        return node;
    if (typeof node === 'number')
        return String(node);
    // Joined with a space, collapsed by the caller: «نام — تاریخ» keeps its own
    // spacing, and two sibling elements do not run together into one word.
    if (Array.isArray(node))
        return node.map(rawText).join(' ');
    if (isValidElement(node)) {
        return rawText(node.props.children);
    }
    return '';
}
function withReadableOptionNames(options, filterProp) {
    if (!Array.isArray(options))
        return options;
    let changed = false;
    const next = options.map((raw) => {
        if (!raw || typeof raw !== 'object')
            return raw;
        const option = raw;
        if (Array.isArray(option.options)) {
            const inner = withReadableOptionNames(option.options, filterProp);
            if (inner === option.options)
                return option;
            changed = true;
            return { ...option, options: inner };
        }
        if (typeof option.label === 'string' || typeof option['aria-label'] === 'string') {
            return option;
        }
        const name = typeof option.title === 'string'
            ? option.title
            : typeof option[filterProp] === 'string' && filterProp !== 'value'
                ? option[filterProp]
                : nodeText(option.label) || null;
        if (!name)
            return option;
        changed = true;
        return { ...option, 'aria-label': name };
    });
    return (changed ? next : options);
}
export function Select({ placement = 'bottom-start', optionFilterProp = 'label', suffixIcon, clearIcon, removeIcon, menuItemSelectedIcon, allowClear, options, virtual, ...rest }) {
    const dir = useUiDirection();
    const readableOptions = useMemo(() => withReadableOptionNames(options, String(optionFilterProp)), [options, optionFilterProp]);
    return (_jsx(AntdSelect, { ...rest, ...(options === undefined ? {} : { options: readableOptions }), virtual: virtual ??
            (options !== undefined && countOptions(options) <= VIRTUAL_THRESHOLD ? false : undefined), optionFilterProp: optionFilterProp, placement: resolveCornerPlacement(placement, dir), suffixIcon: suffixIcon === undefined ? _jsx(Icon, { name: "chevron-down", size: 16 }) : suffixIcon, 
        /**
         * ── `allowClear`, NOT the top-level `clearIcon` ────────────────────────
         *
         * AntD 5.29 logs, on every render of a clearable Select:
         *
         *   Warning: [antd: Select] `clearIcon` is deprecated. Please use
         *   `allowClear={{ clearIcon: React.ReactNode }}` instead.
         *
         * Seen in the console on the console's clinic list, four times per page.
         * The deprecated prop still works today and will stop working at AntD 6,
         * at which point every clearable Select in both panels silently reverts
         * to `@ant-design/icons`. `clearIcon` stays in this wrapper's own props
         * for source compatibility; it is just forwarded through the supported
         * shape now.
         */
        allowClear: allowClear === false || allowClear === undefined
            ? allowClear
            : // `label` is rc-select's accessible name for the clear control. AntD's
                // type omits it, and rc-select falls back to the English «Clear»,
                // which is what a screen reader announced on every filter.
                {
                    label: 'پاک کردن',
                    ...(typeof allowClear === 'object' ? allowClear : {}),
                    clearIcon: (typeof allowClear === 'object' ? allowClear.clearIcon : undefined) ??
                        clearIcon ?? _jsx(Icon, { name: "close", size: 16 }),
                }, removeIcon: removeIcon === undefined ? _jsx(Icon, { name: "close", size: 16 }) : removeIcon, menuItemSelectedIcon: menuItemSelectedIcon === undefined ? _jsx(Icon, { name: "check", size: 16 }) : menuItemSelectedIcon }));
}
Select.Option = AntdSelect.Option;
Select.OptGroup = AntdSelect.OptGroup;
export function TreeSelect({ placement = 'bottom-start', suffixIcon, ...rest }) {
    const dir = useUiDirection();
    return (_jsx(AntdTreeSelect, { ...rest, placement: resolveCornerPlacement(placement, dir), suffixIcon: suffixIcon === undefined ? _jsx(Icon, { name: "chevron-down", size: 16 }) : suffixIcon }));
}
TreeSelect.SHOW_ALL = AntdTreeSelect.SHOW_ALL;
TreeSelect.SHOW_PARENT = AntdTreeSelect.SHOW_PARENT;
TreeSelect.SHOW_CHILD = AntdTreeSelect.SHOW_CHILD;
/**
 * A cascader drills toward the inline END: in RTL the second column appears to
 * the LEFT of the first, and the row's own chevron must point that way.
 * `chevron-forward` is the directional name for "onward in reading order", so
 * it flips with the document and the expansion does not.
 */
/**
 * AntD types `Cascader` as a union discriminated on `multiple`, so a props
 * object that merely *has* `multiple?: boolean` cannot be spread into it. The
 * cast is confined to this one alias and changes nothing at runtime; callers
 * still get the discriminated union through `CascaderProps`.
 */
const CascaderBase = AntdCascader;
export function Cascader({ placement = 'bottom-start', expandIcon, suffixIcon, ...rest }) {
    const dir = useUiDirection();
    return (_jsx(CascaderBase, { ...rest, placement: resolveCornerPlacement(placement, dir), expandIcon: expandIcon === undefined ? _jsx(Icon, { name: "chevron-forward", size: 16 }) : expandIcon, suffixIcon: suffixIcon === undefined ? _jsx(Icon, { name: "chevron-down", size: 16 }) : suffixIcon }));
}
//# sourceMappingURL=select.js.map